import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  ArrowLeft,
  MapPin, 
  Calendar,
  Users,
  User,
  CheckCircle2,
  XCircle,
  Clock,
  Award,
  Phone,
  FileCheck,
  AlertTriangle,
  Crown,
  Briefcase,
  Shield
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminProjectApplications() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [applications, setApplications] = useState([]);
  const [selecting, setSelecting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, app: null });

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/applications`);
      setProject(response.data.project);
      setApplications(response.data.applications);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load project applications");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTeam = async (app) => {
    setSelecting(true);
    try {
      const response = await axios.put(
        `${API}/projects/${projectId}/applications/${app.application_id}/select`
      );
      toast.success(`Team selected! ${response.data.rejected_count} other teams were notified.`);
      setConfirmDialog({ open: false, app: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to select team");
    } finally {
      setSelecting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", icon: Clock, label: "Pending" },
      accepted: { color: "bg-green-500/20 text-green-400 border-green-500/30", icon: CheckCircle2, label: "Selected" },
      not_selected: { color: "bg-gray-500/20 text-gray-400 border-gray-500/30", icon: XCircle, label: "Not Selected" },
      rejected: { color: "bg-red-500/20 text-red-400 border-red-500/30", icon: XCircle, label: "Rejected" },
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout navItems={adminNavItems} title="Project Applications">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  const pendingApps = applications.filter(a => a.status === "pending");
  const selectedApp = applications.find(a => a.status === "accepted");

  return (
    <DashboardLayout navItems={adminNavItems} title="Project Applications">
      <div className="space-y-6" data-testid="admin-project-applications">
        {/* Back Button */}
        <Button 
          onClick={() => navigate("/admin/projects")} 
          variant="ghost" 
          className="text-[#94A3B8] hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Projects
        </Button>

        {/* Project Info */}
        {project && (
          <Card className="bg-gradient-to-br from-[#23F7DD]/10 to-[#4F46E5]/10 border-[#23F7DD]/30">
            <CardHeader>
              <CardTitle className="text-white font-['Unbounded'] text-2xl">
                {project.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="flex items-center gap-2 text-[#94A3B8]">
                  <MapPin className="w-4 h-4 text-[#23F7DD]" />
                  <span>{project.location_city}</span>
                </div>
                <div className="flex items-center gap-2 text-[#94A3B8]">
                  <Calendar className="w-4 h-4 text-[#23F7DD]" />
                  <span>{project.start_date || "TBD"} - {project.end_date || "TBD"}</span>
                </div>
                <div className="flex items-center gap-2 text-[#94A3B8]">
                  <Users className="w-4 h-4 text-[#23F7DD]" />
                  <span>{project.workers_needed || project.required_crew_size || 2} workers needed</span>
                </div>
                <div className="flex items-center gap-2 text-[#23F7DD] font-bold">
                  <Briefcase className="w-4 h-4" />
                  <span>{(project.net_amount_for_workers || project.budget_max || 0).toLocaleString()} NOK</span>
                </div>
              </div>
              
              {project.description && (
                <p className="mt-4 text-[#94A3B8] text-sm">{project.description}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-500/10 rounded-lg">
                  <Users className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-blue-400">
                    {applications.length}
                  </p>
                  <p className="text-sm text-[#94A3B8]">Total Applications</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-500/10 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-yellow-400">
                    {pendingApps.length}
                  </p>
                  <p className="text-sm text-[#94A3B8]">Pending Review</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-500/10 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-green-400">
                    {selectedApp ? 1 : 0}
                  </p>
                  <p className="text-sm text-[#94A3B8]">Selected</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Selected Team Banner */}
        {selectedApp && (
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Award className="w-6 h-6 text-green-400" />
                <div>
                  <p className="text-green-400 font-semibold">Team Selected</p>
                  <p className="text-sm text-[#94A3B8]">
                    {selectedApp.team_leader_name}'s team has been selected for this project.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Applications List */}
        <div className="space-y-4">
          <h2 className="text-xl font-['Unbounded'] font-semibold text-white">
            Applications ({applications.length})
          </h2>

          {applications.length === 0 ? (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-8 text-center">
                <AlertTriangle className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No Applications Yet</h3>
                <p className="text-[#94A3B8]">
                  No teams have applied for this project yet.
                </p>
              </CardContent>
            </Card>
          ) : (
            applications.map((app) => (
              <Card 
                key={app.application_id} 
                className={`bg-white/5 border-white/10 ${
                  app.status === "accepted" ? "ring-2 ring-green-500/50" : ""
                } ${app.status === "pending" ? "hover:border-[#23F7DD]/50 transition-colors" : ""}`}
              >
                <CardContent className="p-6">
                  {/* Header */}
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-full bg-[#23F7DD]/20 flex items-center justify-center">
                        <Crown className="w-7 h-7 text-[#23F7DD]" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-white">
                            {app.team_leader_name}
                          </h3>
                          <span className="px-2 py-0.5 bg-[#23F7DD]/20 text-[#23F7DD] text-xs rounded-full">
                            Team Leader
                          </span>
                        </div>
                        <p className="text-sm text-[#94A3B8]">
                          {app.team_leader_profile?.worker_code || "TL"} • Applied {new Date(app.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {getStatusBadge(app.status)}
                      
                      {app.status === "pending" && !selectedApp && (
                        <Button
                          onClick={() => setConfirmDialog({ open: true, app })}
                          className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold"
                        >
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Select This Team
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Team Members */}
                  <div className="mt-4 p-4 bg-black/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <Users className="w-5 h-5 text-[#23F7DD]" />
                      <span className="text-white font-medium">
                        Team Members ({(app.team_members?.length || 0) + 1} total)
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {/* Team Leader */}
                      <div className="flex items-center gap-3 p-3 bg-[#23F7DD]/10 rounded-lg border border-[#23F7DD]/30">
                        <div className="w-10 h-10 rounded-full bg-[#23F7DD]/20 flex items-center justify-center">
                          <Crown className="w-5 h-5 text-[#23F7DD]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-white font-medium truncate">{app.team_leader_name}</p>
                          <p className="text-xs text-[#23F7DD]">
                            {app.team_leader_profile?.worker_code || "Team Leader"}
                          </p>
                        </div>
                        {app.team_leader_profile?.is_available && (
                          <Shield className="w-4 h-4 text-green-400" title="Available" />
                        )}
                      </div>
                      
                      {/* Team Members */}
                      {app.team_members?.map((member, idx) => (
                        <div 
                          key={member.user_id || idx}
                          className="flex items-center gap-3 p-3 bg-white/5 rounded-lg"
                        >
                          <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                            <User className="w-5 h-5 text-purple-400" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-white font-medium truncate">{member.name}</p>
                            <p className="text-xs text-[#94A3B8]">
                              {member.worker_code || member.profile?.worker_code || "Worker"}
                            </p>
                          </div>
                          {member.profile?.hms_card_status === "verified" && (
                            <FileCheck className="w-4 h-4 text-green-400" title="HMS Verified" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Application Message */}
                  {app.message && (
                    <div className="mt-4 p-3 bg-white/5 rounded-lg">
                      <p className="text-sm text-[#94A3B8]">
                        <span className="text-white font-medium">Message: </span>
                        {app.message}
                      </p>
                    </div>
                  )}

                  {/* Agreement Status */}
                  {app.agreement && (
                    <div className="mt-4 flex items-center gap-2 text-sm">
                      <FileCheck className="w-4 h-4 text-[#23F7DD]" />
                      <span className="text-[#94A3B8]">
                        Agreement: 
                        <span className={`ml-1 ${app.agreement.all_members_signed ? "text-green-400" : "text-yellow-400"}`}>
                          {app.agreement.all_members_signed ? "All Signed" : app.agreement.status}
                        </span>
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Confirm Dialog */}
        <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ open, app: null })}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="font-['Unbounded']">Confirm Team Selection</DialogTitle>
              <DialogDescription className="text-[#94A3B8]">
                You are about to select <strong className="text-white">{confirmDialog.app?.team_leader_name}'s team</strong> for this project.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
                  <div>
                    <p className="text-yellow-400 font-medium">Important</p>
                    <p className="text-sm text-[#94A3B8]">
                      All other applicants ({applications.filter(a => a.application_id !== confirmDialog.app?.application_id && a.status === "pending").length} teams) 
                      will be notified that they were not selected for this project.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <p className="text-green-400 font-medium">Selected team will:</p>
                <ul className="text-sm text-[#94A3B8] mt-2 space-y-1">
                  <li>• Receive a notification about their selection</li>
                  <li>• Be prompted to sign the team agreement</li>
                  <li>• Begin work once all signatures are collected</li>
                </ul>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setConfirmDialog({ open: false, app: null })}
                className="border-white/20 text-[#94A3B8]"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleSelectTeam(confirmDialog.app)}
                disabled={selecting}
                className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold"
              >
                {selecting ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin"></span>
                    Selecting...
                  </span>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Confirm Selection
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
